import { useState, useEffect, useRef } from "react";
import { Button, Checkbox, Modal } from "src/components";
import InvoiceMapping from "./invoice-mapping";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import successCircle from "src/assets/successCircle.svg";
import { useNavigate } from "react-router-dom";
import editPen from "src/assets/edit-pen.svg";
import { useDispatch } from "react-redux";
import { invoiceActions } from "src/context/invoice-slice";
import { autoInvoiceEntry } from "src/services/invoice-api";
import { toast } from "src/hooks/useToast";
import useDebounceLeading from "src/hooks/useDebounceLeading";

const InvoiceProductMapping = () => {
  const [openProductMapping, setOpenProductMapping] = useState(false);
  const [openConfirmInvoice, setOpenConfirmInvoice] = useState(false);
  const [mappingData, setMappingData] = useState<any>([]);
  const { invoicesTextractData, invoiceDetails, invoiceMappingOrderedProducts } = useSelector((state: RootState) => state.invoice);
  const [isOriginalSet, setIsOriginalSet] = useState(false);
  const { invoiceMappingOriginalDescriptions } = useSelector((state: RootState) => state.invoice);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {        //M10-9685-MRP Related Issue
    if (invoicesTextractData.length > 0 && !isOriginalSet && invoiceMappingOriginalDescriptions?.length === 0) {
      const invoiceMappingOriginalDescription = invoicesTextractData.reduce(
        (acc: Record<string, string>, row: any) => {
          if (row?.invoice_product_id && row?.textractProduct_item_description) {
            acc[row.invoice_product_id] = row.textractProduct_item_description;
          }
          return acc;
        },
        {}
      );
      dispatch(invoiceActions.setInvoiceMappingOriginalDescriptions(invoiceMappingOriginalDescription));
      setIsOriginalSet(true);
    }
  }, [invoicesTextractData, isOriginalSet, invoiceMappingOriginalDescriptions]);

  const isRequestInProgress = useRef(false);

  const handleSubmitEntryInvoice = useDebounceLeading(async () => {
    if (isRequestInProgress.current) return;

    isRequestInProgress.current = true;

    try {
      const invoiceId = invoiceDetails?.invoice?.invoice_id;
      const companyCode = invoiceDetails?.company_code;
      const stockistId = invoiceDetails?.stockist_invoice_attachment_id;

      if (!invoiceId || !companyCode) {
        toast({ description: "Missing invoice or company information", variant: "destructive" });
        return;
      }

      const response = await autoInvoiceEntry(
        {
          data: { invoice_id: invoiceId },
          primary_key: invoiceId,
        },
        companyCode
      );

      const { autoEntry, message } = response?.data?.data || {};

      if (autoEntry === false) {
        navigate(`/transactions/invoice/${stockistId}/0`);
      } else {
        toast({ description: message || "Invoice processed successfully." });
        navigate("/transactions/invoice");
      }
    } catch (error: any) {
      console.error("Auto entry error:", error);
      toast({ description: error?.response?.data?.message || "Something went wrong.", variant: "destructive" });
    } finally {
      isRequestInProgress.current = false;
    }
  }, 1000);

  // is_item_unknown === false means the row has been mapped to a real product — either one of
  // the reference order's own items, the catch-all "OTHERS" escalation code, or a company
  // product picked directly via the "Company products" search. All three count as mapped.
  const isRowMapped = (row: any) => row?.is_item_unknown === false;

  // Mapped to the catch-all "OTHERS" master product rather than a real match — flag it visually
  // so the user knows this row needs a closer look, not a genuine product mapping.
  const isOthersMapped = (row: any) => isRowMapped(row) && row?.item_code === "OTHERS";

  const orderItemCodes = new Set(
    (invoiceMappingOrderedProducts || []).map((product: any) => product?.item_code).filter(Boolean)
  );
  // When the invoice has no reference order at all (e.g. INST/KAM module uploads), there's
  // nothing to compare against, so treat every mapped row as "in order" — no warning needed.
  const hasReferenceOrder = (invoiceDetails?.orderCodes?.length || 0) > 0;
  const isProductInReferenceOrder = (row: any) => !hasReferenceOrder || orderItemCodes.has(row?.item_code);

  // "OTHERS" is an intentional escalation code, not a company product pick — exclude it here so
  // the popup only warns about products actually mapped to a company product outside the order.
  const productsNotInReferenceOrder = (invoicesTextractData || []).filter(
    (item: any) => isRowMapped(item) && item?.item_code !== "OTHERS" && !isProductInReferenceOrder(item)
  );

  // M10-15596-M10-15868: normalize before comparing so punctuation, casing, and extra whitespace don't
  // cause a false mismatch against the raw OCR'd invoice text.
  const normalizeForMatch = (text: string) =>
    (text || "")
      .replace(/[^\w]/gi, "")
      .toUpperCase();

  // Only rows mapped via the "Company products" search need this check (is_company_selected is only
  // ever set for those — see invoice-mapping.tsx) — order-list picks are already known-good via the
  // item_code-based productsNotInReferenceOrder check above, and "OTHERS" is an intentional escalation
  // rather than a brand match. This must run regardless of hasReferenceOrder: a reference order only
  // proves the order's own items are fine, it says nothing about a product the user separately picked
  // via search, which can still carry a mismatched brand (M10-15596/M10-15868's "genuine order items"
  // false positives were never reachable here anyway, since those rows never have is_company_selected set).
  // Compares the product's "brand" (e.g. "Ferronemia") rather than item_description, since the
  // latter includes pack-size text (e.g. "Ferronemia 10s pack") that won't appear on the invoice.
  // Exception: if the company-search pick's item_code is itself one of the reference order's own
  // items, it's the same product the order already vouches for — skip the brand text check.
  const companyProductsNotMatchingDescription = (invoicesTextractData || []).filter((row: any) => {
    if (!row?.is_company_selected) return false;
    if (orderItemCodes.has(row?.item_code)) return false;
    const invoiceProductDescription = invoiceMappingOriginalDescriptions[row?.invoice_product_id] || row?.textractProduct_item_description || "";
    const normalizedInvoiceDescription = normalizeForMatch(invoiceProductDescription);
    const normalizedBrand = normalizeForMatch(row?.item_brand);
    return !normalizedBrand || !normalizedInvoiceDescription.includes(normalizedBrand);
  });

  return (
    <div className="min-h-[400px]">
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          textAlign: "left",
          border: "1px solid #ccc",
          // height:"300px"
        }}
        className="mt-10"
      >
        <thead>
          <tr className="text-sm bg-[#F1F1F1]">
            <th style={{ padding: "10px", borderBottom: "1px solid #ccc" }}></th>
            <th style={{ padding: "10px", borderBottom: "1px solid #ccc" }}>Invoice Product Description</th>
            <th style={{ padding: "10px", borderBottom: "1px solid #ccc" }}>Master Product Description</th>
            <th style={{ padding: "10px", borderBottom: "1px solid #ccc" }}>Map</th>
            <th style={{ padding: "10px", borderBottom: "1px solid #ccc" }}>Edit</th>
          </tr>
        </thead>
        <tbody>
          {/* M10-9685-MRP Related Issue - serial no issue */}
          {[...new Map(invoicesTextractData.map((item: any) => [(invoiceMappingOriginalDescriptions[item?.invoice_product_id] || "").replace(/[^\w\s]/gi, ""), item])).values(),].sort((a: any, b: any) => {
            const idA = a?.id || 0;
            const idB = b?.id || 0;
            return idA - idB;
          }).map(
            (row: any, index: any) => (
              (
                <tr key={index} className="w-full" style={{ backgroundColor: isOthersMapped(row) ? "#FFF3CD" : undefined }}>
                  <td style={{ padding: "10px", borderBottom: "1px solid #ccc" }} className="w-[20%] text-gray-500">
                    <Checkbox />
                  </td>
                  <td style={{ padding: "10px", borderBottom: "1px solid #ccc" }} className="w-[20%] text-sm">
                    {invoiceMappingOriginalDescriptions[row?.invoice_product_id] || row?.item_description}
                  </td>
                  <td style={{ padding: "10px", borderBottom: "1px solid #ccc" }} className={`w-[32%] text-sm ${isOthersMapped(row) ? "text-amber-800 font-semibold" : ""}`}>
                    {isRowMapped(row) ? row?.item_description : "-"}
                  </td>
                  {!isRowMapped(row) ? (
                    <td style={{ padding: "10px", borderBottom: "1px solid #ccc" }} className="w-[6%]">
                      <div
                        onClick={() => {
                          setMappingData(row);
                          if (mappingData) {
                            setOpenProductMapping(true);
                          }
                        }}
                      >
                        <button className="px-2 py-1 bg-gray-200 rounded-md">↔</button>
                      </div>
                    </td>
                  ) : (
                    <td style={{ padding: "10px", borderBottom: "1px solid #ccc" }} className="w-[6%]">
                      <img src={successCircle} alt="cross" className="w-5 h-5 font-bold cursor-pointer" />
                    </td>
                  )}
                  <td style={{ padding: "0px", borderBottom: "1px solid #ccc" }} className="w-[6%]">
                    {isRowMapped(row) && <Button onClick={() => {
                      setMappingData(row);
                      if (mappingData) {
                        setOpenProductMapping(true);
                      }
                    }}>
                      <img src={editPen} alt="delete" className="w-4 h-4" />
                    </Button>}
                  </td>
                </tr>
              )
            )
          )}
        </tbody>
      </table>
      <div className="flex justify-between mt-14 gap-x-2">
        <>
          {/* M10-13691
          <Button
            onClick={() => {
              navigate(`/transactions/invoice/${invoiceDetails?.stockist_invoice_attachment_id}/${0}`);
            }}
            className="font-normal bg-gray-200 border border-gray-300"
          >
            Go Manual
          </Button> 
          M10-13691*/}
        </>
        <div className="flex gap-x-2">
          <Button className="font-normal bg-gray-200 border border-gray-300">Escalate to Master</Button>
          <Button
            disabled={isRequestInProgress.current}
            onClick={() => {
              if (invoicesTextractData?.some((item: any) => !isRowMapped(item))) {
                toast({ description: "All products must be mapped to proceed", variant: "destructive" });
                return;
              }
              if (companyProductsNotMatchingDescription.length > 0) {
                //M10-15730
                const mismatchedProductNames = companyProductsNotMatchingDescription
                  .map((row: any) => row?.item_description)
                  .filter(Boolean)
                  .join(", ");
                toast({
                  description: `Selected product/brand does not match the invoice product description: ${mismatchedProductNames}`,
                  variant: "destructive",
                });
                //M10-15730
                return;
              }
              if (productsNotInReferenceOrder.length > 0) {
                setOpenConfirmInvoice(true);
                return;
              }
              handleSubmitEntryInvoice();
            }}
            className="font-normal text-white bg-primary"
          >
            Confirm Invoice
          </Button>
        </div>
      </div>
      <Modal isOpen={openProductMapping} isHeader={false} onCloseModal={() => setOpenProductMapping(false)} title="Escalate Invoice">
        <InvoiceMapping onCloseModal={() => setOpenProductMapping(false)} mappingData={mappingData} />
      </Modal>
      <Modal isOpen={openConfirmInvoice} isHeader={false} onCloseModal={() => setOpenConfirmInvoice(false)}>
        <div className="flex flex-col gap-y-2 mb-4 w-[760px] mx-auto px-6 bg-white p-4 rounded-xl">
          <p className="text-lg font-semibold text-gray-800">Confirm Invoice</p>
          <p className="text-sm text-gray-600 mt-2">
            The following products are not part of the reference order. Do you want to proceed?
          </p>
          <ul className="text-sm text-gray-800 list-disc pl-5 max-h-[150px] overflow-y-auto">
            {productsNotInReferenceOrder.map((item: any, index: number) => (
              <li key={index}>{item?.item_description}</li>
            ))}
          </ul>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              className="bg-gray-300 text-gray-700 hover:bg-[#BFC5CC] px-4 py-2 rounded-lg"
              onClick={() => setOpenConfirmInvoice(false)}
            >
              Cancel
            </Button>
            <Button
              className="text-white bg-primary px-4 py-2 rounded-lg"
              onClick={() => {
                setOpenConfirmInvoice(false);
                handleSubmitEntryInvoice();
              }}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InvoiceProductMapping;
